import {
  createContext,
  useContext,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';
import { useDraggable } from '@dnd-kit/core';
import { HolderOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ticketDraggableId } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';

type TicketDragHandleApi = {
  attributes: ReturnType<typeof useDraggable>['attributes'];
  listeners: ReturnType<typeof useDraggable>['listeners'];
};

const TicketDragHandleContext = createContext<TicketDragHandleApi | null>(null);

function fadeStyle(isDragging: boolean): CSSProperties | undefined {
  return isDragging ? { opacity: 0.35, pointerEvents: 'none' } : undefined;
}

type TicketDragShellProps = {
  ticketId: string;
  ticketCode: string;
  workOrderId: string;
  children: ReactNode;
};

/**
 * Owns `useDraggable`. Re-renders on moves; `children` keep parent element
 * identity so memoized chip chrome does not.
 */
export const TicketDraggableShell: FC<TicketDragShellProps> = ({
  ticketId,
  ticketCode,
  workOrderId,
  children,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ticketDraggableId(ticketId),
    data: {
      type: 'ticket',
      workOrderId,
      ticketId,
      ticketCode,
    },
  });

  return (
    <TicketDragHandleContext.Provider value={{ attributes, listeners }}>
      <div ref={setNodeRef} style={fadeStyle(isDragging)}>
        {children}
      </div>
    </TicketDragHandleContext.Provider>
  );
};

const ticketDragHandleButtonStyle: CSSProperties = {
  flexShrink: 0,
  boxSizing: 'border-box',
  border: 'none',
  background: '#dbeafe',
  color: WORK_ORDER_UI.suggestionLink,
  borderRadius: 6,
  width: 22,
  height: 28,
  padding: 0,
  margin: 0,
  cursor: 'grab',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  lineHeight: 0,
  appearance: 'none',
  WebkitAppearance: 'none',
};

/** Cheap context consumer — updates on drag moves without chip chrome. */
export const TicketChipDragHandle: FC = () => {
  const api = useContext(TicketDragHandleContext);
  const { t } = useTranslation('maintenance');
  const label = t('workOrders.kanban.drag.ticketHandle');

  if (!api) {
    return (
      <span
        aria-hidden
        style={{
          ...ticketDragHandleButtonStyle,
          background: '#e2e8f0',
          opacity: 0.5,
          cursor: 'default',
        }}
      />
    );
  }

  return (
    <Tooltip title={label}>
      <button
        type="button"
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
        style={ticketDragHandleButtonStyle}
        {...api.listeners}
        {...api.attributes}
      >
        <HolderOutlined
          style={{
            fontSize: 12,
            lineHeight: 0,
            display: 'block',
            margin: 0,
          }}
        />
      </button>
    </Tooltip>
  );
};
